/**
 * «Мои сборы» (BLUEPRINT Stage 17, ADR-35): формат турнира и признаки строки для вкладок-фильтров.
 * Состав вкладок выбирает владелец (варианты — `docs/my-gatherings/design.md` и `spec.md`), поэтому
 * функция отдаёт признаки, а не вкладку: «Все · Отряд · Разовые · Турниры» и вариант с отдельной
 * вкладкой сборов ops строятся из одних и тех же полей.
 */

import type { ActivityCode } from '../constants/activities';
import {
  AttendanceKind,
  OrderKind,
  TournamentFormat,
  type AttendanceKind as AttendanceKindT,
  type OrderKind as OrderKindT,
  type TournamentFormat as TournamentFormatT,
} from '../enums';

/** Код активности турнира в каталоге (`ACTIVITIES`). */
export const TOURNAMENT_ACTIVITY_CODE: ActivityCode = 'tournament';

export interface TournamentFormatSource {
  /** `Activity.code` сбора */
  activityCode: string;
  /** `Gathering.tournamentFormat` */
  tournamentFormat: TournamentFormatT | null;
}

/**
 * Формат турнира сбора: `null` — не турнир; у турнира без заданного формата — `STANDARD`
 * (строгий вариант: без решения ops человек видит «участвовать нужно до конца»).
 */
export function tournamentFormatOf(g: TournamentFormatSource): TournamentFormatT | null {
  if (g.activityCode !== TOURNAMENT_ACTIVITY_CODE) return null;
  return g.tournamentFormat ?? TournamentFormat.STANDARD;
}

export interface MyGatheringTagsInput extends TournamentFormatSource {
  /** Человек в активном составе отряда этого сбора */
  isSquadMember: boolean;
  /** `Attendance.kind` человека на сборе, если запись явки есть */
  attendanceKind: AttendanceKindT | null;
  /** Вид позиции заявки (оплаченной или в живой брони), если человек пришёл через заявку */
  orderKind: OrderKindT | null;
  /** `Gathering.opsCreated` */
  opsCreated: boolean;
}

export interface MyGatheringTags {
  relation: AttendanceKindT;
  isOpsExtra: boolean;
  isTournament: boolean;
  tournamentFormat: TournamentFormatT | null;
}

const ORDER_RELATION: Partial<Record<OrderKindT, AttendanceKindT>> = {
  [OrderKind.SINGLE]: AttendanceKind.SINGLE,
  [OrderKind.SUBSTITUTE]: AttendanceKind.SUBSTITUTE,
  [OrderKind.TRIAL]: AttendanceKind.TRIAL,
};

/**
 * Признаки строки «Моих сборов». Вид записи: свой отряд важнее всего (участник на сборе своего
 * отряда — всегда `SQUAD`), затем вид места в явке, затем вид позиции заявки. `null` — сбор
 * человеку не принадлежит (например, ушёл из отряда, а запись явки осталась).
 */
export function myGatheringTags(input: MyGatheringTagsInput): MyGatheringTags | null {
  let relation: AttendanceKindT | null = null;
  if (input.isSquadMember) relation = AttendanceKind.SQUAD;
  else if (input.attendanceKind && input.attendanceKind !== AttendanceKind.SQUAD) {
    relation = input.attendanceKind;
  } else if (input.orderKind) relation = ORDER_RELATION[input.orderKind] ?? null;
  if (relation === null) return null;

  const tournamentFormat = tournamentFormatOf(input);
  return {
    relation,
    isOpsExtra: input.opsCreated,
    isTournament: tournamentFormat !== null,
    tournamentFormat,
  };
}

// ───────────────────────────── флаг по городам ─────────────────────────────

/** Slug города в списке `feature.myGatheringsCities`: как `City.slug` — латиница, цифры, дефис. */
export const CITY_SLUG_RE = /^[a-z0-9][a-z0-9-]{0,63}$/;
/** Сколько городов можно перечислить в списке флага. */
export const FLAG_CITIES_MAX = 50;

/**
 * Включены ли «Мои сборы» для города (Stage 17, задача 9; ADR-47). Общий флаг
 * `feature.myGatherings` — главный выключатель; список `feature.myGatheringsCities` сужает его:
 * пустой — все города (как до списка), непустой — только перечисленные. Человек без города при
 * непустом списке — вне выкатки. Город вне списка видит поведение rc.30.
 */
export function myGatheringsEnabledFor(
  flag: boolean,
  citySlugs: readonly string[],
  citySlug: string | null,
): boolean {
  if (!flag) return false;
  if (citySlugs.length === 0) return true;
  return citySlug !== null && citySlugs.includes(citySlug);
}

/** Разбор ввода ops «moscow, spb» в список slug-ов: без пустых и повторов, в нижнем регистре. */
export function parseCitySlugs(raw: string): string[] {
  return [
    ...new Set(
      raw
        .split(/[\s,;]+/)
        .map((s) => s.trim().toLowerCase())
        .filter((s) => s.length > 0),
    ),
  ];
}
